import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const BANNER_DELAY_MS = 900;

export const DISMISSED_STORAGE_KEY =
  "chekhabareInstallBannerDismissed";

export const DISMISSED_COOKIE_NAME =
  "chekhabare_install_banner_dismissed";

const DISMISSED_VALUE = "1";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const getUserAgent = () => {
  if (typeof navigator === "undefined") {
    return "";
  }

  return navigator.userAgent || "";
};

export const isTopLevelPage = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.top === window.self;
  } catch {
    return false;
  }
};

export const isIosDevice = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = getUserAgent();
  const isRegularIosDevice = /iPhone|iPad|iPod/i.test(
    userAgent
  );

  const isIpadOsDesktopUserAgent =
    navigator.platform === "MacIntel" &&
    Number(navigator.maxTouchPoints || 0) > 1;

  return isRegularIosDevice || isIpadOsDesktopUserAgent;
};

export const isAndroidDevice = () => {
  return /Android/i.test(getUserAgent());
};

export const getMobilePlatform = () => {
  if (isIosDevice()) {
    return "ios";
  }

  if (isAndroidDevice()) {
    return "android";
  }

  return null;
};

export const isStandaloneExperience = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    window.navigator?.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")
        ?.matches ||
      window.matchMedia?.("(display-mode: fullscreen)")
        ?.matches ||
      window.matchMedia?.("(display-mode: minimal-ui)")
        ?.matches
  );
};

export const isAndroidAppShell = () => {
  if (
    typeof window === "undefined" ||
    !isAndroidDevice()
  ) {
    return false;
  }

  const userAgent = getUserAgent();
  const referrer =
    typeof document === "undefined"
      ? ""
      : document.referrer || "";

  const hasWebViewToken = /\bwv\b/i.test(userAgent);
  const matchesAndroidWebViewPattern = Boolean(
    /Android.*;\s*wv\)/i.test(userAgent) ||
      /Android.*Version\/\d+(?:\.\d+)*.*Chrome\/\d+(?:\.\d+)*.*Mobile Safari/i.test(
        userAgent
      )
  );

  return Boolean(
    hasWebViewToken ||
      matchesAndroidWebViewPattern ||
      referrer.startsWith("android-app://") ||
      window.ReactNativeWebView ||
      window.AndroidInterface
  );
};

export const hasDismissalCookie = () => {
  if (typeof document === "undefined") {
    return false;
  }

  try {
    const encodedName = `${encodeURIComponent(
      DISMISSED_COOKIE_NAME
    )}=`;

    return document.cookie
      .split(";")
      .map((part) => part.trim())
      .some((part) => {
        if (!part.startsWith(encodedName)) {
          return false;
        }

        return decodeURIComponent(
          part.slice(encodedName.length)
        ) === DISMISSED_VALUE;
      });
  } catch {
    return false;
  }
};

export const hasDismissedPermanently = () => {
  if (typeof window !== "undefined") {
    try {
      if (
        window.localStorage.getItem(
          DISMISSED_STORAGE_KEY
        ) === DISMISSED_VALUE
      ) {
        return true;
      }
    } catch {
      // Cookie fallback is checked below.
    }
  }

  return hasDismissalCookie();
};

export const persistPermanentDismissal = () => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        DISMISSED_STORAGE_KEY,
        DISMISSED_VALUE
      );
    } catch {
      // Cookie persistence is attempted independently below.
    }
  }

  if (typeof document !== "undefined") {
    try {
      document.cookie = [
        `${encodeURIComponent(
          DISMISSED_COOKIE_NAME
        )}=${DISMISSED_VALUE}`,
        "Path=/",
        `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
        "SameSite=Lax",
      ].join("; ");
    } catch {
      // Installation UI must never crash the application.
    }
  }
};

export const shouldShowBanner = () => {
  const platform = getMobilePlatform();

  if (
    !platform ||
    !isTopLevelPage() ||
    isStandaloneExperience() ||
    (platform === "android" && isAndroidAppShell()) ||
    hasDismissedPermanently()
  ) {
    return false;
  }

  return true;
};

const addMediaQueryListener = (mediaQuery, listener) => {
  if (mediaQuery?.addEventListener) {
    mediaQuery.addEventListener("change", listener);
    return;
  }

  mediaQuery?.addListener?.(listener);
};

const removeMediaQueryListener = (
  mediaQuery,
  listener
) => {
  if (mediaQuery?.removeEventListener) {
    mediaQuery.removeEventListener("change", listener);
    return;
  }

  mediaQuery?.removeListener?.(listener);
};

export function useInstallAppBanner() {
  const platform = useMemo(
    () => getMobilePlatform(),
    []
  );

  const [isVisible, setIsVisible] = useState(false);
  const [isGuideVisible, setIsGuideVisible] =
    useState(false);

  const closeGuide = useCallback(() => {
    setIsGuideVisible(false);
  }, []);

  const dismiss = useCallback(() => {
    setIsGuideVisible(false);
    setIsVisible(false);
  }, []);

  const requestInstall = useCallback(() => {
    if (platform !== "ios" && platform !== "android") {
      return;
    }

    // Keep the existing iOS behavior, but on Android do not
    // permanently dismiss the banner until the APK download is clicked.
    if (platform === "ios") {
      persistPermanentDismissal();
    }

    setIsVisible(false);
    setIsGuideVisible(true);
  }, [platform]);

  const markAndroidDownloadStarted = useCallback(() => {
    if (platform !== "android") {
      return;
    }

    persistPermanentDismissal();
  }, [platform]);

  const hideBecauseInstalled = useCallback(() => {
    persistPermanentDismissal();
    setIsGuideVisible(false);
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (shouldShowBanner()) {
        setIsVisible(true);
      }
    }, BANNER_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const standaloneQuery = window.matchMedia?.(
      "(display-mode: standalone)"
    );

    const fullscreenQuery = window.matchMedia?.(
      "(display-mode: fullscreen)"
    );

    const hideIfInstalled = () => {
      if (
        isStandaloneExperience() ||
        (platform === "android" && isAndroidAppShell())
      ) {
        hideBecauseInstalled();
      }
    };

    const handleAppInstalled = () => {
      hideBecauseInstalled();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        hideIfInstalled();
      }
    };

    window.addEventListener(
      "appinstalled",
      handleAppInstalled
    );
    window.addEventListener("pageshow", hideIfInstalled);
    window.addEventListener("focus", hideIfInstalled);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    addMediaQueryListener(
      standaloneQuery,
      hideIfInstalled
    );
    addMediaQueryListener(
      fullscreenQuery,
      hideIfInstalled
    );

    return () => {
      window.removeEventListener(
        "appinstalled",
        handleAppInstalled
      );
      window.removeEventListener(
        "pageshow",
        hideIfInstalled
      );
      window.removeEventListener("focus", hideIfInstalled);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      removeMediaQueryListener(
        standaloneQuery,
        hideIfInstalled
      );
      removeMediaQueryListener(
        fullscreenQuery,
        hideIfInstalled
      );
    };
  }, [hideBecauseInstalled, platform]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    if (!isGuideVisible) {
      document.body.classList.remove(
        "install-guide-active"
      );
      return undefined;
    }

    document.body.classList.add("install-guide-active");

    return () => {
      document.body.classList.remove(
        "install-guide-active"
      );
    };
  }, [isGuideVisible]);

  return {
    closeGuide,
    dismiss,
    isGuideVisible,
    isVisible,
    markAndroidDownloadStarted,
    platform,
    requestInstall,
  };
}
